import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useEffectivePermissions } from '../../auth/useEffectivePermissions'
import { notify } from '../../store/notificationStore'
import { useBotStore } from './botStore'
import { useBotTurn, useReconcilePendingTurns, useSeedViewMode } from './hooks/useBot'
import { anyNonTerminal, answerHeadline } from './botUtils'
import { BotLauncher } from './BotLauncher'
import { BotPanel } from './BotPanel'
import { BotCompareDock } from './BotCompareDock'

interface TurnWatcherProps {
  turnId: number
}

/**
 * One per pending turn (never hooks-in-a-loop — BotWidget renders one of
 * these per id instead). Polls GET /api/bot/turns/{id} until every answer
 * is terminal, then clears it from the pending list and either marks its
 * answers seen (panel open) or bumps the unread badge + toasts (panel
 * closed). While the panel is open on THIS turn's conversation, BotPanel's
 * own useBotConversation poll already covers it, so polling here is
 * suspended to avoid hitting the same turn twice.
 */
function TurnWatcher({ turnId }: TurnWatcherProps) {
  const { t } = useTranslation('bot')
  const qc = useQueryClient()
  const isOpen = useBotStore((s) => s.isOpen)
  const activeConversationId = useBotStore((s) => s.activeConversationId)
  const activeTab = useBotStore((s) => s.activeTab)
  const seenAnswerIds = useBotStore((s) => s.seenAnswerIds)
  const removePending = useBotStore((s) => s.removePending)
  const bumpUnseen = useBotStore((s) => s.bumpUnseen)
  const markSeen = useBotStore((s) => s.markSeen)

  const knownConversationIdRef = useRef<number | null>(null)
  // `activeConversationId == null` guard: without it, "no conversation
  // selected" (activeConversationId null, knownConversationIdRef.current
  // still null from before this turn's first response lands) reads as
  // activeConversationId === knownConversationIdRef.current — wrongly
  // treated as "already covered by the open conversation's own poll" even
  // though useBotConversation(null) is disabled and polls nothing in that
  // state.
  const shouldPoll = !isOpen || activeConversationId == null || activeConversationId !== knownConversationIdRef.current

  const { data } = useBotTurn(turnId, { poll: shouldPoll })

  useEffect(() => {
    if (data) knownConversationIdRef.current = data.conversationId
  }, [data])

  useEffect(() => {
    if (!data || anyNonTerminal(data)) return
    removePending(turnId)
    // A just-finished answer changes both spend aggregates (the org total
    // and, when this was the caller's own turn, their own share too) —
    // invalidate eagerly so the month chip (BotPanel/BotCompareDock header)
    // and ReportTab don't sit on a stale number until their own
    // staleTime/enabled-refetch happens to fire. Cheap even when this
    // finished turn wasn't the viewer's own: both queries are org-scoped in
    // part, so any completion can move them.
    qc.invalidateQueries({ queryKey: ['bot', 'usage', 'month'] })
    qc.invalidateQueries({ queryKey: ['bot', 'usage', 'report'] })
    // "Seen" means the panel is open, showing THIS answer's conversation,
    // AND on the Chat tab — an answer finishing in a background conversation,
    // or while the user is looking at Memory/Settings instead of Chat, must
    // still bump the badge and toast (P3 added those tabs as alternate
    // content the shell can show while remaining "open" on this conversation).
    const viewing = isOpen && activeConversationId === data.conversationId && activeTab === 'chat'
    for (const answer of data.answers) {
      if (seenAnswerIds.includes(answer.answerId)) continue
      if (viewing) {
        markSeen([answer.answerId])
      } else {
        bumpUnseen(1)
        notify.success(answerHeadline(answer, t))
      }
    }
  }, [data, isOpen, activeConversationId, activeTab, turnId, seenAnswerIds, removePending, bumpUnseen, markSeen, t, qc])

  return null
}

/** Mount root for the Daten-Bot floating widget. Renders nothing unless the
 *  caller can read module 'bot'. See router.tsx (RootLayout, authenticated
 *  branch, sibling of <CommandPalette/>). */
export function BotWidget() {
  const { canRead, isLoading } = useEffectivePermissions()
  const pendingTurnIds = useBotStore((s) => s.pendingTurnIds)
  const activeConversationId = useBotStore((s) => s.activeConversationId)

  // Restore tracking for a still-in-flight turn that a reload dropped from
  // the (deliberately unpersisted) pendingTurnIds — see the hook's own
  // comment in hooks/useBot.ts. Gated on permission state (not just
  // `activeConversationId != null`, which is a persisted store field that
  // outlives a revoked 'bot' role): while permission is still resolving or
  // denied, pass null so the hook's own `enabled: conversationId != null`
  // suppresses the GET /api/bot/conversations/{id} fetch entirely, instead
  // of firing it for a user who is about to be denied rendering below.
  useReconcilePendingTurns(isLoading || !canRead('bot') ? null : activeConversationId)

  // Contract H fix: one-time seed of the live viewMode from the persisted
  // bot.UserSetting.compare_layout, gated the same way as the pending-turn
  // reconciliation above — never fires for a user without 'bot' access, and
  // self-limits to a single fetch per browser via useSeedViewMode's own
  // `viewModeSeeded` check.
  useSeedViewMode(!isLoading && canRead('bot'))

  if (isLoading || !canRead('bot')) return null

  return (
    <>
      <BotLauncher />
      {/* Both shells stay mounted at all times — each gates its own
       *  AnimatePresence content off the persisted viewMode internally
       *  (`shellVisible` in BotPanel.tsx/BotCompareDock.tsx), so a live
       *  popover<->dock swap plays each shell's own enter/exit transition
       *  instead of one being hard-unmounted and the other mounting fresh
       *  with no cross-fade. Both share botStore's `isOpen` (open/close
       *  state carries across a popover<->dock switch). */}
      <BotPanel />
      <BotCompareDock />
      {pendingTurnIds.map((turnId) => (
        <TurnWatcher key={turnId} turnId={turnId} />
      ))}
    </>
  )
}
